import { createContext, useContext, useEffect, useState } from "react";
import api from "@/lib/api";

const AuthContext = createContext({
  user: null,
  loading: true,
  login: async () => {},
  logout: () => {},
});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const token = localStorage.getItem("meetiq_token");
    if (!token) {
      // eslint-disable-next-line
      setLoading(false);
      return undefined;
    }
    api.get("/auth/me")
      .then(({ data }) => {
        if (!cancelled) setUser(data);
      })
      .catch(() => {
        localStorage.removeItem("meetiq_token");
        if (!cancelled) setUser(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  async function login(email, password) {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("meetiq_token", data.token);
    const enriched = {
      ...data.user,
      must_change_password: !!data.must_change_password,
      days_until_expiry: data.days_until_expiry ?? null,
    };
    setUser(enriched);
    return enriched;
  }

  async function registerStart(payload) {
    const { data } = await api.post("/auth/register/start", { ...payload, origin_url: window.location.origin });
    return data;
  }

  async function registerComplete({ token, password }) {
    const { data } = await api.post("/auth/register/complete", { token, password });
    localStorage.setItem("meetiq_token", data.token);
    const enriched = { ...data.user, must_change_password: false, days_until_expiry: null };
    setUser(enriched);
    return enriched;
  }

  async function refreshUser() {
    try {
      const { data } = await api.get("/auth/me");
      setUser((prev) => ({ ...(prev || {}), ...data, must_change_password: false }));
    } catch (_e) { /* noop */ }
  }

  function logout() {
    localStorage.removeItem("meetiq_token");
    setUser(null);
    window.location.href = "/login";
  }

  return (
    <AuthContext.Provider value={{ user, loading, login, registerStart, registerComplete, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
